
import * as THREE from 'three';

export class ParticleSystem {
  private particles: THREE.Points;
  private positions: Float32Array;
  private velocities: Float32Array;
  private colors: Float32Array;
  private count: number;

  constructor(scene: THREE.Scene, count: number = 500) {
    this.count = count;
    
    const geometry = new THREE.BufferGeometry();
    this.positions = new Float32Array(count * 3);
    this.velocities = new Float32Array(count * 3);
    this.colors = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      this.positions[i3] = (Math.random() - 0.5) * 50;
      this.positions[i3 + 1] = (Math.random() - 0.5) * 50;
      this.positions[i3 + 2] = (Math.random() - 0.5) * 50;

      this.velocities[i3] = (Math.random() - 0.5) * 0.02;
      this.velocities[i3 + 1] = (Math.random() - 0.5) * 0.02;
      this.velocities[i3 + 2] = (Math.random() - 0.5) * 0.02;

      const colorChoice = Math.random();
      if (colorChoice < 0.33) {
        this.colors[i3] = 0;
        this.colors[i3 + 1] = 0.96;
        this.colors[i3 + 2] = 0.83;
      } else if (colorChoice < 0.66) {
        this.colors[i3] = 0.48;
        this.colors[i3 + 1] = 0.17;
        this.colors[i3 + 2] = 0.75;
      } else {
        this.colors[i3] = 1;
        this.colors[i3 + 1] = 0.42;
        this.colors[i3 + 2] = 0.42;
      }
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
    });

    this.particles = new THREE.Points(geometry, material);
    scene.add(this.particles);
  }

  update(combo: number = 0): void {
    const positions = this.particles.geometry.attributes.position.array as Float32Array;
    const speedMultiplier = 1 + combo * 0.1;

    for (let i = 0; i < this.count; i++) {
      const i3 = i * 3;

      positions[i3] += this.velocities[i3] * speedMultiplier;
      positions[i3 + 1] += this.velocities[i3 + 1] * speedMultiplier;
      positions[i3 + 2] += this.velocities[i3 + 2] * speedMultiplier;

      if (Math.abs(positions[i3]) > 25) this.velocities[i3] *= -1;
      if (Math.abs(positions[i3 + 1]) > 25) this.velocities[i3 + 1] *= -1;
      if (Math.abs(positions[i3 + 2]) > 25) this.velocities[i3 + 2] *= -1;
    }

    this.particles.geometry.attributes.position.needsUpdate = true;
    this.particles.rotation.y += 0.001;
  }

  burst(): void {
    for (let i = 0; i < this.count; i++) {
      const i3 = i * 3;
      this.velocities[i3] = (Math.random() - 0.5) * 0.1;
      this.velocities[i3 + 1] = (Math.random() - 0.5) * 0.1;
      this.velocities[i3 + 2] = (Math.random() - 0.5) * 0.1;
    }
  }
}

export class FloatingGrid {
  private mesh: THREE.LineSegments;

  constructor(scene: THREE.Scene) {
    const size = 40;
    const divisions = 30;
    const geometry = new THREE.BufferGeometry();
    const positions: number[] = [];

    for (let i = 0; i <= divisions; i++) {
      const t = (i / divisions - 0.5) * size;
      positions.push(-size / 2, -15, t, size / 2, -15, t);
      positions.push(t, -15, -size / 2, t, -15, size / 2);
    }

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    const material = new THREE.LineBasicMaterial({
      color: 0x00f5d4,
      transparent: true,
      opacity: 0.15,
    });

    this.mesh = new THREE.LineSegments(geometry, material);
    scene.add(this.mesh);
  }

  update(time: number): void {
    this.mesh.rotation.y = time * 0.05;
    const material = this.mesh.material as THREE.LineBasicMaterial;
    material.opacity = 0.1 + Math.sin(time) * 0.05;
  }
}

export class GlowingSphere {
  private mesh: THREE.Mesh;
  private glowMesh: THREE.Mesh;

  constructor(scene: THREE.Scene) {
    const geometry = new THREE.IcosahedronGeometry(2, 2);
    const material = new THREE.MeshBasicMaterial({
      color: 0x7b2cbf,
      wireframe: true,
      transparent: true,
      opacity: 0.6,
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.set(0, 0, -20);
    scene.add(this.mesh);

    const glowGeometry = new THREE.IcosahedronGeometry(2.5, 2);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x7b2cbf,
      transparent: true,
      opacity: 0.2,
    });

    this.glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
    this.glowMesh.position.copy(this.mesh.position);
    scene.add(this.glowMesh);
  }

  update(time: number): void {
    this.mesh.rotation.x = time * 0.3;
    this.mesh.rotation.y = time * 0.2;
    this.glowMesh.rotation.x = -time * 0.2;
    this.glowMesh.rotation.y = -time * 0.3;

    const scale = 1 + Math.sin(time * 2) * 0.1;
    this.glowMesh.scale.setScalar(scale);
  }

  pulse(): void {
    this.glowMesh.scale.setScalar(1.5);
  }
}
